import { useAuthentication } from '@ecosystem/newron-client-lib';
import { actions } from '@ecosystem/newron-design-system';
import React, { ReactElement, Suspense, lazy, useContext, useEffect, useRef } from 'react';

import { NotificationToastType, QualtricsWorkflowIds, UpsellConstants, UpsellName } from '../../../common';
import { useQualtricsAPI } from '../../../hooks/useQualtricsAPI';
import { UpsellContext } from '../../../state';
import './SlideWrapper.css';
import { ActionType, setNotificationMessage } from '../../../state/action';
import { handleSurveyButtonClick } from '../../../utils/handleSurveyButtonClick';

const FirstImage = lazy(() => import('../images/FirstImage'));
const SecondImage = lazy(() => import('../images/SecondImage'));
const ThirdImage = lazy(() => import('../images/ThirdImage'));
const FourthImage = lazy(() => import('../images/FourthImage'));

export const SlideWrapper = ({
  children,
  slideNumber,
}: {
  children: ReactElement;
  slideNumber: number;
}): ReactElement => {
  const {
    state: { bannerNumber, activeSlideIndex, retailKADUpsellMessage },
    dispatch,
  } = useContext(UpsellContext);
  const bannerId = useRef<number | undefined>(bannerNumber || undefined);
  const { user } = useAuthentication();
  const { triggerQualtricsWorkflow } = useQualtricsAPI();

  const { Button } = actions;

  useEffect(() => {
    if (bannerNumber !== bannerId.current) {
      bannerId.current = bannerNumber;
    }
  }, [bannerNumber]);

  const onResponseClick = async (response: boolean) => {
    if (retailKADUpsellMessage) {
      try {
        await handleSurveyButtonClick({
          response,
          user,
          upsellContent: retailKADUpsellMessage?.statement3,
          market: retailKADUpsellMessage?.countryCode + '-' + retailKADUpsellMessage?.itemGroupCode,
          country: user?.country,
          urlTokenId: QualtricsWorkflowIds.RETAIL_KAD_UPSELL,
          upsellType: UpsellName.RETAIL_KAD,
          upsellName: UpsellName.RETAIL_KAD,
          triggerQualtricsWorkflow,
        });
        dispatch(
          setNotificationMessage({
            message: UpsellConstants.ToastMessageSuccess,
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      } catch (error) {
        dispatch(
          setNotificationMessage({
            message: UpsellConstants.ToastMessageError,
            type: NotificationToastType.Error,
            timeout: 3000,
          })
        );
      } finally {
        dispatch({ type: ActionType.SetActiveSlideIndex, payload: 0 });
        dispatch({ type: ActionType.SetIsModalOpen, payload: false });
      }
    }
  };

  return (
    <div>
      <div className="retail-kad-slide flex justify-center items-center" data-test-id="retail-kad-slide">
        {children}
      </div>
      {activeSlideIndex === slideNumber - 1 && (
        <>
          <div className="retail-kad-slide-container">
            <p className="retail-kad-slide-text text-md mt-2">
              Register your interest today and your account manager will be in touch with more information
            </p>
            <Button testId="qualtrics-positive" onClick={() => onResponseClick(true)}>
              Yes I'm interested
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export const RetailSlidesFirst = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <SlideWrapper slideNumber={1}>
        <div>
          <FirstImage />
        </div>
      </SlideWrapper>
    </Suspense>
  );
};

export const RetailSlidesSecond = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <SlideWrapper slideNumber={2}>
        <div>
          <SecondImage />
        </div>
      </SlideWrapper>
    </Suspense>
  );
};

export const RetailSlidesThird = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <SlideWrapper slideNumber={3}>
        <div>
          <ThirdImage />
        </div>
      </SlideWrapper>
    </Suspense>
  );
};

export const RetailSlidesFourth = (): React.ReactElement => {
  return (
    <Suspense fallback={null}>
      <SlideWrapper slideNumber={4}>
        <div>
          <FourthImage />
        </div>
      </SlideWrapper>
    </Suspense>
  );
};

export const RetailFirstSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="retail-slide-header" className="text-2xl">
      Grow brand reach with granular data for clear decisions
    </span>
  );
};

export const RetailSecondSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="retail-slide-header" className="text-2xl">
      Master your category with holistic partner insights
    </span>
  );
};

export const RetailThirdSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="retail-slide-header" className="text-2xl">
      Seize every opportunity with retailer ecosystem analysis
    </span>
  );
};

export const RetailFourthSlideHeader = (): React.ReactElement => {
  return (
    <span data-test-id="retail-slide-header" className="text-2xl">
      Enhance your key account management
    </span>
  );
};

export const RetailSurveyHeader = (): React.ReactElement => {
  return (
    <span data-test-id="retail-slide-header" className="text-2xl">
      How do you find out more?
    </span>
  );
};
